import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { roundTaxDollars as roundTaxDollarsShared, splitInclusiveTotal as splitInclusiveTotalShared } from 'src/app/services/studio-tax.service';
import { BaseDialogComponent } from '../../forms/base-dialog/base-dialog.component';
import { environment } from 'src/environments/environment';
import { MatStepper } from '@angular/material/stepper';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Validators, FormArray, AbstractControl, ValidationErrors } from '@angular/forms';
import { EnrollmentEntityComponent } from '../../forms/enrollment-entity/enrollment-entity.component';
import { EventLessonEntityComponent } from '../../forms/event-lesson-entity/event-lesson-entity.component';
import { from, mergeMap, forkJoin } from 'rxjs';
import { getEnrollmentAgreementBackpageContent } from '../../../../assets/text/enrollment-agreement-backpage.content';
import { LocalizationService } from 'src/app/services/localization.service';
import moment from 'moment';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-base-enrollment',
  templateUrl: './base-enrollment.component.html'
})
export class BaseEnrollmentComponent extends BaseDialogComponent {
  private localizationService = new LocalizationService();

  public backpageHtmlContent: string = '';

  detectSignatureStudio: boolean;
  detectSignatureStudent: boolean;
  detectSignatureParent: boolean;
  flag1: boolean;
  flag2: boolean;

  tosAccepted: boolean = false;

  // Enrollment agreement template key ('amdc_standard' | 'amdc_italian' | 'amdc_australian').
  // Set by subclasses; drives which back page the print/display generator emits.
  activeTemplateKey: string = 'amdc_standard';

  studioInformationConfig;
  taxRate: number = 0;
  // True when the studio's active percentage taxes are inclusive (tax already
  // inside the enrollment price). Drives the "(includes tax)" wording and the
  // Canadian templates' balance-due math (exclusive tax is added on top).
  isTaxInclusive: boolean = false;
  // Raw configuration:tax rows for the studio (source of isTaxInclusive and the agreement tax label).
  taxConfig: any[] = [];

  EventLessonEntityComponent = EventLessonEntityComponent;
  EnrollmentEntityComponent = EnrollmentEntityComponent;

  enrollmentStepperIndex: number = 0;

  @ViewChild('SignatureStudent') signatureStudent: ElementRef<HTMLCanvasElement>;
  @ViewChild('SignatureStudio') signatureStudio: ElementRef<HTMLCanvasElement>;
  @ViewChild('SignatureParent') signatureParent: ElementRef<HTMLCanvasElement>;
  @ViewChild('enrollmentStepper') enrollmentStepper: MatStepper;

  /**
   * Custom validator to ensure package name follows the format "Package Name (ID)"
   */
  private packageFormatValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const value = control.value;

    // Check if value is a number (means it's just an ID)
    if (!isNaN(+value)) {
      return null; // Valid - just an ID
    }

    // Check if value contains (ID) pattern
    const regExp = /\(([^)]+)\)/g;
    const matches = value.match(regExp);

    if (!matches || matches.length === 0) {
      return { invalidPackageFormat: true };
    }

    return null; // Valid format
  }

  /**
   * Custom validator to ensure the student account is a selected account:
   * either a bare id or the autocomplete's "Student Name (ID)" text. Free
   * text typed without picking an option used to pass the required check,
   * __alterValues() then turned it into undefined, the request went out
   * without field_student and the enrollment was saved with no student.
   */
  private studentFormatValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const value = control.value;

    if (typeof value !== 'string') {
      return null; // Prefilled reference objects are resolved elsewhere
    }

    // Check if value is a number (means it's just an ID)
    if (/^\d+$/.test(value.trim())) {
      return null; // Valid - just an ID
    }

    // Check if value ends with a numeric (ID) pattern
    if (/\(\d+\)\s*$/.test(value)) {
      return null; // Valid format
    }

    return { invalidStudentFormat: true };
  }

  override ngOnInit(): void {
    this.initActionType = this._injectedDialogData['data']['action'];
    this.actionType = this._injectedDialogData['data']['action'];
    this.enrollmentID = this._injectedDialogData['data']['EntityID'];

    // Load extra data for enrollments.
    if (this._injectedDialogData['data']['action'] == 'view' || this._injectedDialogData['data']['action'] == '0' || this._injectedDialogData['data']['action'] == 'signEnrollment') {
      this.loadEnrollmentData(this.enrollmentID).subscribe(data => {
        this._enrollmentEntityData = data;

        // Load additional student data once we know which enrollment we are working with.
        // Handle both array format (old API) and object format (new API)
        let studentId;
        if (Array.isArray(data['field_student'])) {
          // Old format: array with target_id
          studentId = data['field_student'][0]?.target_id;
        } else if (data['field_student'] && typeof data['field_student'] === 'object') {
          // New format: object with id
          studentId = data['field_student'].id || data['field_student'].target_id;
        }

        if (studentId) {
          this._entityRESTService.getEntity('student_accounts', 'student_account', studentId)
            .subscribe(data => {
              this.studentAccountEntityData = data;
              // Render the backpage (Terms of Service, Schedule A, Schedule B) once
              // the data it needs is loaded. Whichever async result lands last wins;
              // the method no-ops until both student and studio data are present.
              this.generateBackpageDisplayContent();
            });
        }
      });

      this._drupalRESTService.httpGET('/api_rest/v1/getStudioInformation')
        .subscribe(data => {
          this.studioInformationConfig = data;
          LoggerService.log('Studio Information:', this.studioInformationConfig);
          this.generateBackpageDisplayContent();
        }, error => {
          LoggerService.error('Error fetching studio information:', error);
        });
    }

    // Load extra data for enrollments.
    if (this._injectedDialogData['data']['action'] == 'signEnrollment') {
      // this.enrollmentStepper.selectedIndex = 3;
      this.loadEnrollmentData(this.enrollmentID).subscribe(data => {
        this._enrollmentEntityData = data;

        // Load additional student data once we know which enrollment we are working with.
        // Handle both array format (old API) and object format (new API)
        let studentId;
        if (Array.isArray(data['field_student'])) {
          // Old format: array with target_id
          studentId = data['field_student'][0]?.target_id;
        } else if (data['field_student'] && typeof data['field_student'] === 'object') {
          // New format: object with id
          studentId = data['field_student'].id || data['field_student'].target_id;
        }

        if (studentId) {
          this._entityRESTService.getEntity('student_accounts', 'student_account', studentId)
            .subscribe(data => {
              // LoggerService.log('student_accounts student_account data', data);
              this.studentAccountEntityData = data;

              if (this.enrollmentStepper != undefined) {
                this.enrollmentStepper.selectedIndex = 2;
              }
            });
        }
      });
    }

    this.enrollmentForm = this._formBuilder.group({
      title: ['default title'],
      field_draft: [false],
      field_student: [this._frmValues?.field_student, [Validators.required, this.studentFormatValidator.bind(this)]],
      field_sale_date: [this._frmValues?.field_sale_date, Validators.required],
      field_expiration_date: [null, Validators.required],
      field_category: [null, Validators.required],
      field_enrollment_package_name: [null, [Validators.required, this.packageFormatValidator.bind(this)]],
      field_enrollment_lesson_price: [null, Validators.required],
      field_enrollment_lesson_count: [null, Validators.required],
      field_total_lessons_price: ['', Validators.required],
      field_enrollment_total_price: [null, Validators.required],
      field_total_price_with_est_tax: [null, Validators.required],
      field_taxes: [null, Validators.required],
      field_tax_percentage: [this.taxRate, Validators.required],
      field_total_paid: [null],
      field_lesson_used: [null],
      field_lessons_paid: [null],
      field_lesson_available: [null],
      field_payments_structure: [],
      field_payments_structure_type: [],
      // UI-only: drives the installment generator. The down payment itself is
      // persisted as the first scheduled-payment row (amount + field_down_payment),
      // not as a top-level enrollment field. Blank/0 = split the full total.
      field_down_payment_amount: [0],
      field_executive: [null, Validators.required],
      field_junior_executive: [null],
      field_scheduled_payments: new FormArray([]),
      field_instructor_percentages: new FormArray([]),
      field_interval_unit: [''],
      field_discount: [0, Validators.required],
      field_discount_type: [],
      field_enrollment_status: ['Open', Validators.required],
      field_next_scheduled_payment: [''],
      field_visibility: [false],
      field_notes: [''],
      field_tos_accepted_date: [null],
      interval: [this.interval],
      // Installment builder: split the remainder into a number of payments
      // ('count', the default) or into payments of an agreed amount ('amount',
      // the last payment taking whatever is left). UI-only, like interval.
      installment_split_mode: ['count'],
      installment_amount: [null],
      field_recurrent_month_day: [''],
      field_recurrent_weekday_mo: [''],
      field_recurrent_weekday_tu: [''],
      field_recurrent_weekday_we: [''],
      field_recurrent_weekday_th: [''],
      field_recurrent_weekday_fr: [''],
      field_recurrent_weekday_sa: [''],
      field_recurrent_weekday_su: [''],
      payment_builder_date_step: new FormArray([])
    });
    this.agreementFormGroup = this._formBuilder.group({
      field_nothing: [''],
    });
    this.paymentForm = this._formBuilder.group({
      field_payment_id: [this.paymentID, Validators.required],
      // field_customer_name: [null],
      field_date_and_time: [this.todaysDate, Validators.required],
      field_gross_tuition: [null, Validators.required],
      field_student_name: [null, Validators.required],
      field_enrollment_name: this._formBuilder.group({
        'target_id': [null, Validators.required]
      }),
      field_scheduled_payment: this._formBuilder.group({
        'target_id': [null]
      }),
      field_payment_type: [null, Validators.required],
      cc_number: [null],
      cc_cvv: [null],
      cc_expiration: [null],
      cc_expiration_month: [null],
      cc_expiration_year: [null],
      cc_postal_code: [null],
      field_stripe_charge_id: [this.entityData?.field_stripe_charge_id],
      reason_to_refund: [null],
      field_payments_use_card: [null],
      field_notes: [null],
    });
  }

  formatDisplayDate(value: string | number | Date | null | undefined, fallback: string = ''): string {
    if (!value) {
      return fallback;
    }

    const countryCode = this.studioInformationConfig?.field_studio_address?.[0]?.country_code || 'US';
    const formats = this.localizationService.getDateFormatsForCountry(countryCode);
    const normalizedValue = value instanceof Date ? moment(value) : moment(String(value), moment.ISO_8601, true);
    const parsed = normalizedValue.isValid() ? normalizedValue : moment(value);

    return parsed.isValid() ? parsed.format(formats.calendarDateFormat) : fallback;
  }

  override ngAfterViewInit(): void {
    this.agreementFormGroup = this._formBuilder.group({
      field_nothing: [''],
    });

    // Safely initialize signatures
    setTimeout(() => {
      // Only initialize student and studio signatures by default
      if (this.signatureStudent) {
        this.signatureInit(this.signatureStudent, 'student');
      }
      if (this.signatureStudio) {
        this.signatureInit(this.signatureStudio, 'studio');
      }
      // Parent signature will be initialized when it becomes visible
    }, 0);

    // Pull current tax rate.
    if (this.actionType == "create") {
      this._fieldsService.getTaxRateConfig().subscribe(data => {
        this.enrollmentForm.get("field_tax_percentage").setValue(data?.[0]?.['value']);
      });
    } else {
      // Respect the tax value if in edit mode.
    }

    if (this.actionType == 'edit') {
      LoggerService.log('loadContactData');
      this.loadContactData();
    }

    this.getUniquePaymentID()
      .subscribe(data => {
        this.paymentID = data;
        // this.setFormMode("create");
      });
  }

  setStepperIndex(index) {
    // this.enrollmentStepper.linear = true;
    if (this.enrollmentStepper != undefined) {
      this.enrollmentStepper.selected.completed = true;
      this.enrollmentStepper.selected.editable = false;
      this.enrollmentStepper.next();
    }
  }

  referenceUploadedFileComplete(action) {
    if (action === 'save_and_pay') {
      // Autofill payment information
      this.paymentForm.patchValue({
        field_date_and_time: this.enrollmentForm.get('field_sale_date').value,
        field_student_name: this.enrollmentEntityData['field_student']['title'] + ' (' + this.enrollmentEntityData['field_student']['id'] + ')',
        field_enrollment_name: {
          target_id: this.enrollmentEntityData['id'],
        },
      });

      this.displayProgressSpinner(false);
    }
  }

  signatureInit(elementRef: ElementRef<HTMLCanvasElement>, elementName: string, retriesLeft = 20) {
    // Safety check - if elementRef is undefined or has no nativeElement, return early
    if (!elementRef || !elementRef.nativeElement) {
      LoggerService.log(`Canvas element for ${elementName} is not available yet`);
      return;
    }

    let canvas = elementRef.nativeElement;

    // Guard against being initialized before layout is settled (slow Android
    // tablets, or a canvas inside a not-yet-active stepper step). If the element
    // has no laid-out size yet, retry on the next frame instead of baking a 0x0
    // bitmap (which silently captures a blank signature). Bounded so it can't spin
    // forever if the canvas is genuinely never displayed.
    if ((!canvas.offsetWidth || !canvas.offsetHeight) && retriesLeft > 0) {
      requestAnimationFrame(() => this.signatureInit(elementRef, elementName, retriesLeft - 1));
      return;
    }

    // Belt-and-suspenders: set touch-action directly on the element in JS, not
    // only via the CSS class. On Chrome for Android the CSS may not be applied at
    // the exact moment the first touch lands; if touch-action isn't "none" when
    // the gesture starts, Chrome runs its scroll-vs-draw disambiguation and fires
    // pointercancel a moment into the stroke -> the classic "line breaks a few
    // seconds after signing". Setting it inline guarantees it is in effect.
    canvas.style.touchAction = 'none';

    // Size the bitmap to match the displayed size BEFORE getting the context and
    // setting styles. Assigning canvas.width/height resets the 2D context to its
    // defaults, so it must happen first or lineWidth/lineCap/strokeStyle get wiped.
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    let context = canvas.getContext('2d');

    if (!context) { return; }

    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = 'black';
    context.lineWidth = 3;

    let x = 0, y = 0;
    let isDrawing = false;

    // Use Pointer Events exclusively. Pointer events unify mouse, touch and
    // stylus input into a single stream (not the touch* + pointer* double-fire the
    // original code produced, which drew every stroke twice in mismatched
    // coordinate spaces). clientX/Y minus the canvas rect, scaled to the bitmap
    // resolution, gives a single correct coordinate space for every device.
    const getCoordinates = (event: PointerEvent): [number, number] => {
      const rect = canvas.getBoundingClientRect();
      // rect.width can momentarily be 0 during a reflow; fall back to bitmap size
      // (scale 1) rather than dividing by zero and producing NaN coordinates.
      const scaleX = rect.width ? canvas.width / rect.width : 1;
      const scaleY = rect.height ? canvas.height / rect.height : 1;
      return [
        (event.clientX - rect.left) * scaleX,
        (event.clientY - rect.top) * scaleY,
      ];
    };

    // Draw a segment from the current point to a new one.
    const drawTo = (newX: number, newY: number) => {
      context.beginPath();
      context.moveTo(x, y);
      context.lineTo(newX, newY);
      context.stroke();
      x = newX;
      y = newY;
    };

    const stopDrawing = (event?: PointerEvent) => {
      isDrawing = false;
      if (event) {
        try { canvas.releasePointerCapture(event.pointerId); } catch { /* already released */ }
      }
    };

    const startDrawing = (event: PointerEvent) => {
      // Prevent default browser behavior like scrolling/touch-pan.
      event.preventDefault();

      // Capture the pointer so the stroke keeps tracking the finger/stylus even if
      // it drifts past the (small) canvas edge mid-signature, instead of aborting.
      try { canvas.setPointerCapture(event.pointerId); } catch { /* not all pointers are capturable */ }

      isDrawing = true;
      [x, y] = getCoordinates(event);
      // Draw a dot at the start so a tap (down+up with no move) still registers ink.
      drawTo(x + 0.01, y + 0.01);

      if (elementName === 'student') {
        this.detectSignatureStudent = true;
      }
      if (elementName === 'studio') {
        this.detectSignatureStudio = true;
      }
      if (elementName === 'parent') {
        this.detectSignatureParent = true;
      }
    };

    const drawLine = (event: PointerEvent) => {
      if (!isDrawing) return;

      // Prevent default browser behavior like scrolling/touch-pan.
      event.preventDefault();

      // Use coalesced events: on fast strokes Android/Chrome batches several moves
      // into one pointermove. Replaying them all instead of just the final point
      // avoids the stroke rendering as a series of straight jumps (a "broken line").
      const events = (typeof event.getCoalescedEvents === 'function')
        ? event.getCoalescedEvents()
        : [];
      if (events.length > 0) {
        for (const e of events) {
          const [nx, ny] = getCoordinates(e as PointerEvent);
          drawTo(nx, ny);
        }
      } else {
        const [newX, newY] = getCoordinates(event);
        drawTo(newX, newY);
      }
    };

    // If the browser cancels the pointer mid-stroke (e.g. it decided the gesture
    // was a scroll despite touch-action:none, or a system gesture interrupted),
    // end the current segment but DO NOT wipe what was already drawn - the detect*
    // flag stays true so the partial signature still uploads and the signer can
    // simply continue with a new stroke, rather than losing everything.
    const cancelDrawing = (event: PointerEvent) => { stopDrawing(event); };

    // passive: false is required so preventDefault() can suppress touch scrolling.
    const addEventOptions = { passive: false };

    canvas.addEventListener('pointerdown', startDrawing, addEventOptions);
    canvas.addEventListener('pointermove', drawLine, addEventOptions);
    canvas.addEventListener('pointerup', stopDrawing);
    canvas.addEventListener('pointercancel', cancelDrawing);
    // No 'pointerleave' end: pointer capture (set in startDrawing) keeps the stroke
    // tracking the finger/stylus past the canvas edge, so the stroke ends only on
    // pointerup/pointercancel - matching native signature-pad behavior.
  }

  signatureClear(elementRef: ElementRef<HTMLCanvasElement>) {
    const canvas = elementRef;
    if (!canvas || !canvas.nativeElement) {
      LoggerService.error('Canvas element is undefined or invalid');
      return;
    }

    const context = canvas.nativeElement.getContext('2d');
    if (!context) {
      LoggerService.error('Could not get 2D context for canvas');
      return;
    }

    // Clear the entire canvas area
    context.clearRect(0, 0, context.canvas.width, context.canvas.height);
  }

  signatureClearStudent() {
    this.signatureClear(this.signatureStudent);
    this.detectSignatureStudent = false;
  }

  signatureClearStudio() {
    this.signatureClear(this.signatureStudio);
    this.detectSignatureStudio = false;
  }

  signatureClearParent() {
    this.signatureClear(this.signatureParent);
    this.detectSignatureParent = false;
  }

  printPage(backPageOnly = false, promptPrint = false) {
    let printContents, popupWin, printContentsBackpage;

    // Check if essential data for backpage is loaded
    if (!this.studentAccountEntityData || !this.studioInformationConfig) {
      LoggerService.error('Print called before student or studio data loaded.');
      this._snackBar.open('Data required for printing is not yet loaded. Please wait and try again.', 'Close', { duration: 4000 });
      return; // Stop execution if data isn't ready
    }

    printContents = document.getElementById('enrollment-agreement')?.innerHTML;
    if (!printContents) {
      printContents = document.getElementById('enrollment-agreement-new')?.innerHTML;
    }

    // Check if content already contains a logo to avoid duplicates
    const hasLogo = printContents && printContents.includes('AMDCBlackHorizontalJPG.jpg');

    // Prepare data object
    const saleDateSource = this._enrollmentEntityData?.field_sale_date?.[0]?.value || this.enrollmentForm.get('field_sale_date')?.value;
    const formattedSaleDate = this.formatDisplayDate(saleDateSource);

    const tosAcceptedRaw = this._enrollmentEntityData?.['field_tos_accepted_date']?.[0]?.value
      || this.entityData?.field_tos_accepted_date;

    const backpageData = {
      // Use fallbacks directly in the object creation if needed, but the check above is primary
      studentName: this.studentAccountEntityData?.title || '[Student Name Missing]',
      studioName: this.studioInformationConfig?.field_studio_display_name?.[0]?.value || '[Studio Name Missing]',
      agreementDate: formattedSaleDate || '[Date Missing]',
      bondingInfo: this.studioInformationConfig?.field_studio_is_bonded, // Pass raw value to be processed
      // Embed captured signatures on Schedule A (mirrors page 1) + e-sign attestation.
      studentSignatureUrl: this._enrollmentEntityData?.['field_signature']?.[0]?.['url'],
      studioSignatureUrl: this._enrollmentEntityData?.['field_representative_signature']?.[0]?.['url'],
      tosAcceptedDate: tosAcceptedRaw ? this.formatDisplayDate(tosAcceptedRaw) : undefined,
      templateKey: this.activeTemplateKey,
      // Corporation/legal name (Italian ToS §6 non-compete), same address
      // sub-field the Australian template uses for its corporate-entity line.
      organizationName: this.studioInformationConfig?.field_studio_address?.[0]?.organization,
      studioCity: this.studioInformationConfig?.field_studio_address?.[0]?.locality
    };
    LoggerService.log('backpageData', backpageData);

    // Call the function with data
    printContentsBackpage = getEnrollmentAgreementBackpageContent(backpageData);

    popupWin = window.open('', '_blank', 'top=0,left=0,height=100%,width=auto');
    if (popupWin) {
      popupWin.document.open();
      popupWin.document.write(`
        <html>
          <head>
            <title>Enrollment Copy</title>
            <link href="assets/vendor/bootstrap/css/bootstrap.min.css" rel="stylesheet">
            <style type="text/css">
              /* Standard print margins - compact to fit ~4 pages */
              @page {
                size: letter portrait;
                margin: 0.5in;
              }

              body {
                font-family: Arial, Helvetica, sans-serif;
                font-size: 9px !important;
                line-height: 1.15 !important;
                color: #000;
                margin: 0;
                padding: 0.1in;
              }

              body * {
                font-size: inherit !important;
                line-height: inherit !important;
              }

              h4, h5, h6 {
                font-size: 11px !important;
                margin-top: 0.3rem !important;
                margin-bottom: 0.2rem !important;
              }

              h4 {
                font-size: 13px !important;
              }

              p {
                margin-bottom: 0.15rem !important;
              }

              .row {
                margin-bottom: 0.1rem !important;
              }

              .mb-1, .mb-2, .mb-3, .mb-4 {
                margin-bottom: 0.1rem !important;
              }

              .mt-1, .mt-2, .mt-3, .mt-4 {
                margin-top: 0.1rem !important;
              }

              .p-3 {
                padding: 0.3rem !important;
              }

              .container {
                max-width: 100% !important;
                padding: 0 0.5in !important;
              }

              .padding-main-divcls{
                padding: 2px;
              }

              .text-center{
                text-align: center;
              }
              .width-full{
                width: 100%;
              }

              .box {
                  border-style: solid;
                  border-width: 1px;
                  width: 65px;
                  height: 100px;
                  float: right;
                  margin-right: 50px;
                  font-size: 10px;
                  padding: 5px;
              }
              .box-divcls {
                width: 100%;
                display: inline-block;
              }

              .TermsConditionTable, tr , td {
                padding: 2px !important;
              }
              tr, td {
                page-break-inside: avoid !important;
              }
              .display-none {
                display:none;
              }

              .page1 {
                overflow: hidden;
              }

              .page2, .page3, .page4 { /* Apply base styles to all pages */
                font-size: 9.5px;
                line-height: 1.2;
                page-break-before: always; /* Ensure back pages start on new print pages */
              }
              .page2:first-child, .page3:first-child, .page4:first-child { /* Avoid break before the very first element if needed */
                 page-break-before: auto;
              }

              /* Print container styling */
              .print-container {
                max-width: 8in;
                margin: 0 auto;
              }

              @media print {
                .print-d-none {
                  display: none !important; /* Hide elements with this class when printing */
                }

                @page {
                  margin: 0.5in;
                }

                body {
                  padding: 0;
                  font-size: 9px !important;
                  line-height: 1.15 !important;
                }
              }

              /* Enrollment agreement display styles */
              .border-bottom {
                border-bottom: 1px solid #000 !important;
                display: inline-block;
              }

              .payoff-section {
                padding: 8px 0;
              }

              .payoff-row {
                display: flex;
                align-items: flex-start;
                gap: 8px;
                margin-bottom: 6px;
              }

              .payoff-row:last-child {
                margin-bottom: 0;
              }

              .payoff-options {
                display: flex;
                gap: 6px;
                flex-shrink: 0;
              }

              .payoff-note {
                flex: 1;
                min-width: 0;
                color: #000;
              }

              /* Black-and-white for print: browsers drop backgrounds and smear
                 blurred shadows, so the checked state is a check glyph. */
              .choice-box {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                padding: 2px 6px;
                border: 1px solid #000;
                border-radius: 3px;
                background: #fff;
                color: #000;
                font-weight: 600;
                white-space: nowrap;
              }

              .choice-square {
                width: 11px;
                height: 11px;
                border: 1px solid #000;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
                background: #fff;
              }

              .choice-box.is-checked .choice-square::after {
                content: '\\2713';
                font-size: 10px;
                line-height: 1;
                font-weight: 700;
                color: #000;
              }

              .choice-label {
                text-transform: uppercase;
              }

            </style>
          </head>
            <body>
              ${!hasLogo ? '<p class="text-center"><img style="max-width:350px;" src="assets/images/AMDCBlackHorizontalJPG.jpg" /></p>' : ''}
              <div class="container">
                ${backPageOnly ? printContentsBackpage : printContents}
                ${!backPageOnly ? printContentsBackpage : ''}
              </div>
            </body>
        </html>
      `)
      popupWin.document.close();

      // Wait for the agreement to actually render before printing. Chrome/Edge
      // print a BLANK page if print() fires first, and this page embeds a logo
      // image, so a fixed delay was a guess that lost the race on slow loads.
      // Close only after a real print completes: tearing the window down while
      // "Save as PDF" is still pending can drop the job.
      if (promptPrint) {
        popupWin.onafterprint = function () { popupWin.close(); };
        popupWin.onload = function () {
          const fire = function () {
            popupWin.requestAnimationFrame(function () {
              popupWin.requestAnimationFrame(function () { popupWin.print(); });
            });
          };
          const fonts = (popupWin.document as any).fonts;
          if (fonts && fonts.ready) {
            fonts.ready.then(fire).catch(fire);
          } else {
            fire();
          }
        };
      }
    } else {
        this._snackBar.open('Could not open print window. Please check browser pop-up settings.', 'Close', { duration: 5000 });
    }
  }

  /**
   * Resolve isTaxInclusive from the studio's tax configuration without touching
   * the form. Used by read-only surfaces (e.g. the standalone signing page)
   * that render a saved enrollment and need to know whether field_taxes sits
   * inside or on top of field_enrollment_total_price.
   */
  loadTaxInclusiveFlag(): void {
    this._drupalRESTService.httpGET('/api_rest/v1/loadStudioConfig?config_field_name=field_tax_ref').subscribe(
      (response: any) => {
        const taxes = Array.isArray(response) ? response : (response ? [response] : []);
        this.taxConfig = taxes;
        const now = moment();
        this.isTaxInclusive = taxes.some(tax => {
          const val = (f: string) => Array.isArray(tax?.[f]) ? tax[f][0]?.value : tax?.[f];
          const active = val('field_tax_status') === '1' || val('field_tax_status') === 'true';
          if (!active) return false;
          const start = val('field_effective_tax_start_date');
          const end = val('field_effective_tax_end_date');
          if (start && now.isBefore(moment(start))) return false;
          if (end && now.isAfter(moment(end))) return false;
          if (val('field_calculation_method') !== 'PERCENTAGE') return false;
          const inc = val('field_is_tax_inclusive');
          return inc === '1' || inc === 'true' || inc === true;
        });
      },
      () => { /* keep default (exclusive) */ }
    );
  }

  // The studio's tax rows as resolved for this enrollment's sale date (set by
  // the enrollment form's config resolution): each tax's percentage, whether
  // it's inside the price, and how its dollar amount rounds.
  activeTaxRows: Array<{ rate: number; inclusive: boolean; roundingRule: string }> | undefined;

  /**
   * Round a dollar amount to cents by the configured rule. The amount is
   * snapped in cent space first so an exact half cent ($100 × 9.975% = $9.975)
   * rounds by the rule, not by its binary representation ($9.97499999…).
   */
  protected roundTaxDollars(value: number, rule: string): number {
    return roundTaxDollarsShared(value, rule);
  }

  /**
   * Tax dollars on one amount, split into the exclusive part (added on top of
   * the price) and the inclusive part (already inside it).
   *
   * When the resolved tax rows are available and the form still holds their
   * combined rate, each tax is computed and rounded SEPARATELY by its own
   * configured rounding rule: the way receipts present stacked taxes
   * (Quebec GST + QST) and the way the payment form stores its breakdown, so
   * the enrollment estimate matches what recording the payment will collect.
   * A manually-typed Taxes % (or a stored rate the config no longer matches)
   * has no per-tax split, so it falls back to the combined-rate math.
   */
  protected computeTaxParts(amount: number): { exclusive: number; inclusive: number } {
    const formRate = parseFloat(this.enrollmentForm?.get('field_tax_percentage')?.value) || 0;
    const rows = this.activeTaxRows;
    const configuredRate = (rows || []).reduce((sum, row) => sum + row.rate, 0);

    if (rows?.length && Math.abs(formRate - configuredRate) < 0.0005) {
      // Stacked inclusive taxes (Quebec GST + QST both inside the price)
      // must extract against the COMBINED rate: each tax is r_i% of the
      // pre-tax base = amount / (1 + R_incl). Extracting each independently
      // (amount − amount/(1+r_i)) counts the other tax's share twice and
      // overstates the total ($676.20 would yield $93.53 instead of $88.07).
      const inclusiveRateSum = rows.reduce((sum, row) => sum + (row.inclusive ? row.rate : 0), 0);
      let exclusive = 0;
      let inclusive = 0;
      for (const row of rows) {
        if (row.inclusive) {
          inclusive += this.roundTaxDollars(amount * (row.rate / 100) / (1 + inclusiveRateSum / 100), row.roundingRule);
        } else {
          exclusive += this.roundTaxDollars(amount * (row.rate / 100), row.roundingRule);
        }
      }
      // Sums of cents are already cents. The final round only strips float noise.
      return {
        exclusive: this.roundTaxDollars(exclusive, 'ROUND_NEAREST'),
        inclusive: this.roundTaxDollars(inclusive, 'ROUND_NEAREST'),
      };
    }

    const inclusiveTaxRate = (this as any).inclusiveTaxRate || 0;
    const exclusiveTaxRate = (this as any).exclusiveTaxRate || 0;

    if (inclusiveTaxRate > 0 && exclusiveTaxRate > 0) {
      return {
        inclusive: this.roundTaxDollars(amount - amount / (1 + inclusiveTaxRate / 100), 'ROUND_NEAREST'),
        exclusive: this.roundTaxDollars(amount * (exclusiveTaxRate / 100), 'ROUND_NEAREST'),
      };
    } else if (inclusiveTaxRate > 0) {
      return {
        inclusive: this.roundTaxDollars(amount - amount / (1 + inclusiveTaxRate / 100), 'ROUND_NEAREST'),
        exclusive: 0,
      };
    }
    return {
      exclusive: this.roundTaxDollars(amount * (formRate / 100), 'ROUND_NEAREST'),
      inclusive: 0,
    };
  }

  calculateEstimatedTax(paymentAmount: number): number {
    const parts = this.computeTaxParts(paymentAmount);
    return this.roundTaxDollars(parts.exclusive + parts.inclusive, 'ROUND_NEAREST');
  }

  calculateTax(index: number) {
    const paymentControl = (this.enrollmentForm.get('field_scheduled_payments') as FormArray).at(index);
    const paymentAmount = parseFloat(paymentControl.get('field_payment_amount').value) || 0;
    const taxAmount = this.calculateEstimatedTax(paymentAmount);
    paymentControl.patchValue({ field_tax_amount: taxAmount });
  }

  /**
   * The scheduled-row "Total (incl. tax)" entry column shows for studios that
   * add tax on top of prices (Canada, US sales tax). Inclusive-tax studios'
   * Payment Amount already IS the customer total, and untaxed studios have
   * nothing to back out.
   */
  get scheduledRowsShowTotal(): boolean {
    const inclusive = (this as any).isTaxInclusive;
    const rate = parseFloat(this.enrollmentForm?.get('field_tax_percentage')?.value) || 0;
    return !inclusive && rate > 0;
  }

  /** Customer-facing total for one scheduled row: stored amount + stored tax. */
  scheduledRowTotal(index: number): number {
    const row = (this.enrollmentForm.get('field_scheduled_payments') as FormArray)?.at(index);
    const amount = parseFloat(row?.get('field_payment_amount')?.value) || 0;
    const tax = parseFloat(row?.get('field_tax_amount')?.value) || 0;
    return Math.round((amount + tax) * 100) / 100;
  }

  /**
   * Staff typed the amount the customer will actually hand over for one
   * installment: back out the pre-tax amount and tax so they always sum to
   * exactly the typed figure (a typed $150.00 stays $150.00; the forward
   * math can't always reproduce a round figure; same guarantee as the
   * payment screen's Total box).
   */
  onScheduledTotalEntered(index: number, rawValue: any): void {
    const total = parseFloat(String(rawValue ?? '').replace(/[^0-9.\-]/g, ''));
    if (isNaN(total)) {
      return;
    }
    const row = (this.enrollmentForm.get('field_scheduled_payments') as FormArray).at(index);
    const split = this.splitInclusiveTotal(Math.round(total * 100) / 100);
    row.patchValue({
      field_payment_amount: split.gross.toFixed(2),
      field_tax_amount: split.tax,
    });
    this.reconcileScheduleCentsToEnrollment(index);
  }

  /**
   * Each typed row Total is split into pre-tax + tax on its own, so across
   * several rows the pre-tax amounts can drift from the enrollment's pre-tax
   * total by rounding even though every Total is exact. When the drift is
   * within rounding, move it between gross and tax on the edited row: the
   * typed Total is unchanged and the schedule sums to the enrollment exactly.
   * Larger differences are real mismatches and are left visible.
   */
  protected reconcileScheduleCentsToEnrollment(index: number): void {
    const rows = this.enrollmentForm.get('field_scheduled_payments') as FormArray;
    const target = Math.round((parseFloat(this.enrollmentForm.get('field_enrollment_total_price')?.value) || 0) * 100) / 100;
    if (!rows?.length || !(target > 0)) {
      return;
    }
    const grossSum = rows.controls.reduce((sum, r) => sum + (parseFloat(r.get('field_payment_amount')?.value) || 0), 0);
    const diff = Math.round((target - grossSum) * 100) / 100;
    if (diff === 0 || Math.abs(diff) > 0.02) {
      return;
    }
    const row = rows.at(index);
    const gross = parseFloat(row.get('field_payment_amount')?.value) || 0;
    const tax = parseFloat(row.get('field_tax_amount')?.value) || 0;
    if (tax - diff < 0) {
      return;
    }
    row.patchValue({
      field_payment_amount: (Math.round((gross + diff) * 100) / 100).toFixed(2),
      field_tax_amount: Math.round((tax - diff) * 100) / 100,
    });
  }

  /**
   * Exact split of a typed tax-inclusive total: each exclusive tax extracted
   * as its share of the total (r_i / (1 + combined rate), rounded by its
   * configured rule), the pre-tax amount takes the remainder, so
   * gross + tax always reproduces the typed figure to the cent. Falls back
   * to the form's combined rate when the resolved rows are unavailable
   * (edit mode, manual rate override).
   */
  protected splitInclusiveTotal(total: number): { gross: number; tax: number } {
    // Same shared split the payment screen uses, so a row's pre-tax amount and
    // the gross recorded when its Total is paid can never differ by a cent.
    const rows = (this.activeTaxRows ?? []).filter(r => !r.inclusive);
    const formRate = parseFloat(this.enrollmentForm?.get('field_tax_percentage')?.value) || 0;
    const configuredRate = rows.reduce((sum, r) => sum + r.rate, 0);

    if (rows.length && Math.abs(formRate - configuredRate) < 0.0005) {
      return splitInclusiveTotalShared(total, rows.map(r => ({ rate: r.rate, roundingRule: r.roundingRule })));
    }
    if (formRate > 0) {
      return splitInclusiveTotalShared(total, [{ rate: formRate, roundingRule: 'ROUND_NEAREST' }]);
    }
    return { gross: Math.round(total * 100) / 100, tax: 0 };
  }

  getPaymentStructureTypeLabel(): string {
    const type = this.entityData?.field_payments_structure_type;
    switch (type) {
      case 'paid_in_full':
        return 'Pay in full';
      case 'weekly':
        return 'Weekly';
      case 'monthly':
        return 'Monthly';
      case 'by_the_lesson':
        return 'By the lesson';
      default:
        return '';
    }
  }

  loadContactData() {
    if (this.studentAccountEntityData && this.studentAccountEntityData.field_contacts) {
      // The field_contacts might be an object with 'target_id' or a direct ID reference
      let contactId = null;

      if (typeof this.studentAccountEntityData.field_contacts === 'object') {
        // Check if it's an array with target_id
        if (Array.isArray(this.studentAccountEntityData.field_contacts) &&
          this.studentAccountEntityData.field_contacts.length > 0) {
          if (this.studentAccountEntityData.field_contacts[0].target_id) {
            contactId = this.studentAccountEntityData.field_contacts[0].target_id;
          } else if (this.studentAccountEntityData.field_contacts[0].id) {
            contactId = this.studentAccountEntityData.field_contacts[0].id;
          }
        } else if (this.studentAccountEntityData.field_contacts.target_id) {
          // Direct object with target_id
          contactId = this.studentAccountEntityData.field_contacts.target_id;
        } else if (this.studentAccountEntityData.field_contacts.id) {
          // Direct object with id
          contactId = this.studentAccountEntityData.field_contacts.id;
        }
      } else {
        // It might be a direct ID value
        contactId = this.studentAccountEntityData.field_contacts;
      }

      if (contactId) {
        LoggerService.log('Loading contact data for ID:', contactId);
        this._entityRESTService.getEntity('contacts', 'student_record', contactId)
          .subscribe(contactData => {
            // Store the contact data directly in studentAccountEntityData.field_contacts
            // This ensures backward compatibility with existing template references
            this.studentAccountEntityData.field_contacts = contactData;
            LoggerService.log('Contact data loaded:', contactData);
          }, error => {
            LoggerService.error('Error loading contact data:', error);
          });
      } else {
        LoggerService.warn('Could not determine contact ID from student account data');
      }
    } else {
      LoggerService.warn('No field_contacts found in student account data');
    }
  }

  /**
 * Updates the field_tos_accepted_date when the Terms of Service checkbox is checked
 * @param event The change event from the checkbox
 */
  updateTosAcceptedDate(event: MatCheckboxChange) {
    // Update the tosAccepted property immediately to update the UI
    this.tosAccepted = event.checked;

    if (event.checked) {
      // Format the date to match the required format without milliseconds
      const now = new Date();
      // Use the helper method to format the date
      const currentDatetime = this.formatDateForDrupal(now);

      // If we're in create mode, update the form value
      if (this.actionType === 'create') {
        this.enrollmentForm.patchValue({
          field_tos_accepted_date: currentDatetime
        });
        LoggerService.log('TOS accepted date updated in form:', currentDatetime);
      } else if (this.entityData?.id) {
        // If we're in edit mode, update the entity directly
        const values = {
          field_tos_accepted_date: currentDatetime
        };

        // Immediately update the local entityData for UI responsiveness
        if (this.entityData) {
          this.entityData.field_tos_accepted_date = currentDatetime;
        }

        this._entityRESTService.patchEntity(
          this.eckType,
          this.bundle,
          this.entityData.id,
          values
        ).subscribe(
          response => {
            LoggerService.log('TOS accepted date updated:', response);
            // this._snackBar.open('Terms of Service accepted', 'Close', {
            //   duration: 3000,
            // });
          },
          error => {
            LoggerService.error('Error updating TOS accepted date:', error);
            // Rollback the UI change if the API call fails
            if (this.entityData) {
              this.entityData.field_tos_accepted_date = null;
              this.tosAccepted = false;
            }
            this._snackBar.open('Error updating Terms of Service acceptance', 'Close', {
              duration: 3000,
            });
          }
        );
      }
    } else {
      // Checkbox is unchecked - clear the TOS accepted date

      // If we're in create mode, update the form value
      if (this.actionType === 'create') {
        this.enrollmentForm.patchValue({
          field_tos_accepted_date: null
        });
        LoggerService.log('TOS accepted date cleared in form');
      } else if (this.entityData?.id) {
        // If we're in edit mode, update the entity directly
        const values = {
          field_tos_accepted_date: null
        };

        // Immediately update the local entityData for UI responsiveness
        if (this.entityData) {
          this.entityData.field_tos_accepted_date = null;
        }

        this._entityRESTService.patchEntity(
          this.eckType,
          this.bundle,
          this.entityData.id,
          values
        ).subscribe(
          response => {
            LoggerService.log('TOS accepted date cleared:', response);
            // this._snackBar.open('Terms of Service acceptance withdrawn', 'Close', {
            //   duration: 3000,
            // });
          },
          error => {
            LoggerService.error('Error clearing TOS accepted date:', error);
            // Rollback in case of error
            this.tosAccepted = true;
            this._snackBar.open('Error updating Terms of Service acceptance', 'Close', {
              duration: 3000,
            });
          }
        );
      }
    }
  }

  /**
* Formats a date to the required format for the Drupal backend (YYYY-MM-DDThh:mm:ss)
* @param date The date to format
* @returns Formatted date string
*/
  formatDateForDrupal(date: Date): string {
    // Format the date in local time instead of UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    // Format as YYYY-MM-DDThh:mm:ss
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  async onSubmitSignatures(action) {
    this.displayProgressSpinner(true);
    if (action !== 'save_and_add' && action !== 'save_and_email') {
      this.setStepperIndex(2);
    }

    const toBlob = (canvasElement: HTMLCanvasElement): Promise<Blob> =>
      new Promise((resolve) => canvasElement.toBlob(resolve));

    const handleBlob = (fieldName: string, element: ElementRef<HTMLCanvasElement>) =>
      from(toBlob(element.nativeElement)).pipe(
        mergeMap((blob) =>
          this._drupalRESTService
            .uploadFile('packages', 'enrollment', fieldName, environment.name + '_' + fieldName + '.png', blob)
            .pipe(
              mergeMap((data) => {
                const values = {
                  [fieldName]: [
                    {
                      target_id: data['fid']?.[0]?.['value'],
                      description: '',
                    },
                  ],
                };

                const entityId = this.entityData?.['id'] ?? this._enrollmentEntityData?.['id'];
                if (typeof entityId !== 'undefined') {
                  return this._entityRESTService.patchEntity(
                    this.eckType,
                    this.bundle,
                    entityId,
                    values
                  );
                }

                throw new Error('Missing entity ID.');
              })
            )
        )
      );

    let observables = [];

    // Check if student signature is not present before attempting to upload
    if (this.detectSignatureStudent && !this._enrollmentEntityData?.['field_signature']?.[0]?.['url']) {
      observables.push(handleBlob('field_signature', this.signatureStudent));
    }

    // Check if representative signature is not present before attempting to upload
    if (this.detectSignatureStudio && !this._enrollmentEntityData?.['field_representative_signature']?.[0]?.['url']) {
      observables.push(handleBlob('field_representative_signature', this.signatureStudio));
    }

    // Check if parent signature is not present before attempting to upload
    if (this.detectSignatureParent && this.signatureParent && this.signatureParent.nativeElement &&
      !this._enrollmentEntityData?.['field_parent_guardian_signature']?.[0]?.['url']) {
      // Check if there's actually something drawn on the canvas
      this.checkParentSignature(this.signatureParent.nativeElement);

      if (this.detectSignatureParent) {
        observables.push(handleBlob('field_parent_guardian_signature', this.signatureParent));
      }
    }

    if (observables.length > 0) {
      forkJoin(observables).subscribe(
        () => {
          this.referenceUploadedFileComplete(action);
          this.displayProgressSpinner(false);

          if (action === 'save_and_add') {
            this._snackBar.open('Signatures saved successfully', 'Close', {
              duration: 3000,
            });
            this.closeDialog();
          } else if (action === 'save_and_email') {
            // Only email once the signature uploads/patches have committed.
            // Emailing first sends an enrollment the backend can't fully resolve.
            this.sendEmailNewEnrollment(this.entityData?.['id'] ?? this._enrollmentEntityData?.['id']);
          }
        },
        (err) => {
          LoggerService.error(err);
          this.displayProgressSpinner(false);
          this._snackBar.open('Error saving signatures', 'Close', {
            duration: 3000,
          });
        }
      );
    } else {
      this.displayProgressSpinner(false);

      if (action === 'save_and_add') {
        this._snackBar.open('Enrollment saved successfully', 'Close', {
          duration: 3000,
        });
        this.closeDialog();
      } else if (action === 'save_and_pay') {
        this.referenceUploadedFileComplete(action);
      } else if (action === 'save_and_email') {
        // Nothing new to upload (signatures already persisted), so safe to email directly.
        this.sendEmailNewEnrollment(this.entityData?.['id'] ?? this._enrollmentEntityData?.['id']);
      }
    }
  }

  checkParentSignature(canvas: HTMLCanvasElement) {
    // Get a snapshot of the canvas data
    const context = canvas.getContext('2d');
    if (!context) return;

    // Get image data to check if there's something drawn
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Check if the canvas has any non-white/non-transparent pixels
    let hasSignature = false;
    for (let i = 0; i < data.length; i += 4) {
      // If any pixel has a non-zero alpha value and is not white
      if (data[i + 3] > 0 && !(data[i] === 255 && data[i + 1] === 255 && data[i + 2] === 255)) {
        hasSignature = true;
        break;
      }
    }

    // Update detection state
    this.detectSignatureParent = hasSignature;
  }

  sendEmailNewEnrollment(enrollmentID) {
    this._drupalRESTService.httpGET('/api_rest/v1/sendNewEnrollmentEmail?enrollmentID=' + enrollmentID)
      .subscribe(
        data => {
          LoggerService.log('API response:', data);
          if (data['status'] === 'error') {
            const errorMessage = data['message'] || 'An error occurred while sending the email.';
            // Snackbar message that is centered.
            this._snackBar.open(errorMessage, 'Close', {
              duration: 3000,
              horizontalPosition: 'center',
            });
          } else {
            // Snackbar message that is centered.
            this._snackBar.open('Email sent successfully.', 'Close', {
              duration: 3000,
              horizontalPosition: 'center',
            });
          }
        },
        error => {
          LoggerService.error('Error sending email:', error);
          // Snackbar message that is centered.
          this._snackBar.open('An error occurred while sending the email.', 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
          });
        }
      );
  }

  generateBackpageDisplayContent() {
    if (this.studentAccountEntityData && this.studioInformationConfig) {
        const saleDateSource = this._enrollmentEntityData?.field_sale_date?.[0]?.value || this.enrollmentForm.get('field_sale_date')?.value;
        const formattedSaleDate = this.formatDisplayDate(saleDateSource);

        const tosAcceptedRaw = this._enrollmentEntityData?.['field_tos_accepted_date']?.[0]?.value
            || this.entityData?.field_tos_accepted_date;

        const backpageData = {
            studentName: this.studentAccountEntityData?.title || '[Student Name Missing]',
            studioName: this.studioInformationConfig?.field_studio_display_name?.[0]?.value || '[Studio Name Missing]',
            agreementDate: formattedSaleDate || '[Date Missing]',
            bondingInfo: this.studioInformationConfig?.field_studio_is_bonded || '',
            // Embed captured signatures on Schedule A (mirrors page 1) + e-sign attestation.
            studentSignatureUrl: this._enrollmentEntityData?.['field_signature']?.[0]?.['url'],
            studioSignatureUrl: this._enrollmentEntityData?.['field_representative_signature']?.[0]?.['url'],
            tosAcceptedDate: tosAcceptedRaw ? this.formatDisplayDate(tosAcceptedRaw) : undefined,
            templateKey: this.activeTemplateKey,
            // Corporation/legal name (Italian ToS §6 non-compete), same address
            // sub-field the Australian template uses for its corporate-entity line.
            organizationName: this.studioInformationConfig?.field_studio_address?.[0]?.organization,
            studioCity: this.studioInformationConfig?.field_studio_address?.[0]?.locality
        };
        this.backpageHtmlContent = getEnrollmentAgreementBackpageContent(backpageData);
    } else {
        // Handle case where data isn't ready yet
        this.backpageHtmlContent = '<p>Loading agreement details...</p>';
    }
}
}
